import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

export default function Signup() {
  const [organizationName, setOrganizationName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerPassword, setOwnerPassword] = useState('');
  const [ownerDisplayName, setOwnerDisplayName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const { signup, error } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    // Validation
    if (ownerPassword.length < 8) {
      setLocalError('Password must be at least 8 characters long');
      return;
    }

    setIsLoading(true);

    try {
      await signup(organizationName, ownerEmail, ownerPassword, ownerDisplayName || undefined);
      navigate('/');
    } catch (err) {
      // Error is handled by useAuth
    } finally {
      setIsLoading(false);
    }
  };

  const displayError = localError || error;

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h1 style={styles.title}>Create Organization</h1>
          <p style={styles.subtitle}>KPCS & KSFM Live Translation Service</p>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          {displayError && (
            <div style={styles.error}>
              {displayError}
            </div>
          )}

          <div style={styles.inputGroup}>
            <label htmlFor="organizationName" style={styles.label}>
              Organization Name *
            </label>
            <input
              id="organizationName"
              type="text"
              value={organizationName}
              onChange={(e) => setOrganizationName(e.target.value)}
              required
              disabled={isLoading}
              style={styles.input}
              placeholder="My Organization"
            />
          </div>

          <div style={styles.inputGroup}>
            <label htmlFor="ownerEmail" style={styles.label}>
              Email *
            </label>
            <input
              id="ownerEmail"
              type="email"
              value={ownerEmail}
              onChange={(e) => setOwnerEmail(e.target.value)}
              required
              disabled={isLoading}
              style={styles.input}
              autoComplete="email"
              placeholder="admin@example.com"
            />
          </div>

          <div style={styles.inputGroup}>
            <label htmlFor="ownerPassword" style={styles.label}>
              Password * (min. 8 characters)
            </label>
            <input
              id="ownerPassword"
              type="password"
              value={ownerPassword}
              onChange={(e) => setOwnerPassword(e.target.value)}
              required
              minLength={8}
              disabled={isLoading}
              style={styles.input}
              autoComplete="new-password"
            />
          </div>

          <div style={styles.inputGroup}>
            <label htmlFor="ownerDisplayName" style={styles.label}>
              Display Name (optional)
            </label>
            <input
              id="ownerDisplayName"
              type="text"
              value={ownerDisplayName}
              onChange={(e) => setOwnerDisplayName(e.target.value)}
              disabled={isLoading}
              style={styles.input}
              placeholder="Admin User"
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            style={{
              ...styles.button,
              ...(isLoading ? styles.buttonDisabled : {}),
            }}
          >
            {isLoading ? 'Creating organization...' : 'Create Organization'}
          </button>

          <div style={styles.footer}>
            <span style={styles.footerText}>Already have an account?</span>
            <Link to="/login" style={styles.link}>
              Sign in
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: '20px',
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    padding: '40px',
    width: '100%',
    maxWidth: '400px',
  },
  header: {
    textAlign: 'center',
    marginBottom: '32px',
  },
  title: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#1a202c',
    margin: '0 0 8px 0',
  },
  subtitle: {
    fontSize: '14px',
    color: '#718096',
    margin: 0,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  error: {
    padding: '12px',
    backgroundColor: '#fee',
    border: '1px solid #fcc',
    borderRadius: '6px',
    color: '#c00',
    fontSize: '14px',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  label: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#2d3748',
  },
  input: {
    padding: '10px 12px',
    border: '1px solid #cbd5e0',
    borderRadius: '6px',
    fontSize: '14px',
    transition: 'border-color 0.2s',
    outline: 'none',
  },
  button: {
    padding: '12px',
    backgroundColor: '#667eea',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  buttonDisabled: {
    backgroundColor: '#a0aec0',
    cursor: 'not-allowed',
  },
  footer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '8px',
    marginTop: '8px',
  },
  footerText: {
    fontSize: '14px',
    color: '#718096',
  },
  link: {
    fontSize: '14px',
    color: '#667eea',
    textDecoration: 'none',
    fontWeight: '500',
  },
};
